import { CompareIcon, DeleteIcon, DownloadIcon, MixIcon, UploadIcon } from '@assets/icons';
import { ActionButton } from '@components/button';
import { Group } from '@components/layout';
import { useConfirmation, useModal } from '@components/modal';
import { useDeleteDatasetMutation, useDownloadDatasetMutation } from '@generated/graphql';
import { downloadFromUrl } from '@helper-functions/save-file';
import { useNotifications } from '@notifications/Notifications';
import { DatasetsActionUploadModal, DatasetsCompareModal, useDatasets } from '@subsets/workspaces';
import posthog from 'posthog-js';
import { useState } from 'react';
import { MixDatasetsModal } from './MixDatasetsModal';

interface DatasetsActionsProps {
  workspaceId: string;
}

export const DatasetsActions = ({ workspaceId }: DatasetsActionsProps) => {
  const { selectedDatasets, setSelectedDataset, setSelectedDatasets, datasets, pagination } =
    useDatasets();
  const { showConfirmation } = useConfirmation();
  const { createAsyncNotification } = useNotifications();
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const handleCompareOnClick = () => {
    if (selectedDatasets.length >= 2) {
      posthog.capture('user_create-umap_intent');
      setCompareModalOpen(true);
    }
  };
  const selected = selectedDatasets.length > 0;
  const oneFileSelected = selectedDatasets.length === 1;

  const [downloadDataset] = useDownloadDatasetMutation();
  const [deleteDataset] = useDeleteDatasetMutation();

  const { showModal: showUploadDatasetModal } = useModal({
    component: DatasetsActionUploadModal,
    componentProps: { workspaceId },
    modalProps: { title: 'Upload Dataset' },
  });

  const { showModal: showMixDatasetsModal } = useModal({
    component: MixDatasetsModal,
    componentProps: { workspaceId, selectedDatasets, refetch: pagination.refetch },
    modalProps: { title: 'Mix Datasets', sx: { width: '80%', maxWidth: '900px' } },
  });

  const handleDownloadDataset = createAsyncNotification(
    'Download started - check your browser downloads',
    async () => {
      const response = await downloadDataset({
        variables: {
          workspaceId,
          datasetId: selectedDatasets[0],
        },
      });

      const datasetName = datasets.filter((d) => d.datasetId === selectedDatasets[0])[0].name;

      await downloadFromUrl({ url: response.data.downloadDataset, name: `${datasetName}.zip` });
    },
    { loadingMessage: 'Starting download...' },
  );

  const handleDeleteDatasets = () => {
    showConfirmation({
      onAffirm: createAsyncNotification('Datasets deleted', async () => {
        await Promise.all(
          selectedDatasets.map(async (id: string) => {
            const res = await deleteDataset({
              variables: {
                workspaceId,
                datasetId: id,
              },
            });
            return res;
          }),
        );
        setSelectedDatasets([]);
        setSelectedDataset(null);
        pagination.refetch();
      }),
      message: `Are you sure you want to delete the selected dataset${selectedDatasets.length > 1 ? 's' : ''}?`,
    });
  };

  return (
    <Group alignItems="center" gap={2} ml={2}>
      <ActionButton
        Icon={UploadIcon}
        label="Upload"
        onClick={() => {
          posthog.capture('user_upload-dataset_intent');
          showUploadDatasetModal();
        }}
      />
      <ActionButton
        Icon={DownloadIcon}
        label="Download"
        onClick={oneFileSelected ? handleDownloadDataset : undefined}
        disabled={!oneFileSelected}
      />
      <ActionButton
        Icon={DeleteIcon}
        label="Delete"
        onClick={handleDeleteDatasets}
        disabled={!selected}
      />
      <ActionButton
        Icon={CompareIcon}
        label="Compare"
        onClick={handleCompareOnClick}
        disabled={selectedDatasets.length < 2}
      />
      <DatasetsCompareModal
        open={compareModalOpen}
        workspaceId={workspaceId}
        onClose={() => setCompareModalOpen(false)}
      />
      <ActionButton Icon={MixIcon} label="Mix" onClick={showMixDatasetsModal} />
    </Group>
  );
};
